import { Component } from 'react';

import { CheckBox } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { getCountry } from '../../../../../../iwIntlProvider/CountryProvider';
import IwMedia from '../../../../../../iwMedia/index';
import { CLOSE_MODAL } from '../../../../../../iwModals/Modals.actions';
import ConfirmationButton from '../../../../../ui/confirmationButton/ConfirmationButton';
import DownloadButton from '../../../../../ui/downloadButton/DownloadButton';
import { Prompt } from '../../../../../ui/prompt';
import { ScrollPanel } from '../../../../../ui/scrollPanel/ScrollPanel';
import {
    SIGN_DOCUMENT_AGREE_AND_CONTINUE_ACTION,
    SIGN_DOCUMENT_BOTTOM_VISIBLE,
    TOGGLE_DOCUMENT_CHECKBOX
} from '../../SignDocuments.actions';
import styles from '../SignDocumentModals.module.css';
import SigningDocumentContentPending from '../SigningDocumentContentPending';

const mapStateToProps = ({ drawDown }) => ({
    ...drawDown
});

const mapDispatchToProps = (dispatch) => ({
    cancel: () => dispatch({ type: CLOSE_MODAL }),
    onBottomVisible: (document) =>
        dispatch({ type: SIGN_DOCUMENT_BOTTOM_VISIBLE, document }),
    toggleCheckbox: (isDocumentConsentAgreed) =>
        dispatch({ type: TOGGLE_DOCUMENT_CHECKBOX, isDocumentConsentAgreed }),
    agreeAndContinue: (document) =>
        dispatch({ type: SIGN_DOCUMENT_AGREE_AND_CONTINUE_ACTION, document })
});

class SignDocumentsActionModal extends Component {
    handleCancel = () => {
        this.props.cancel(this.props.signingDocumentId);
    };

    handleAgree = () => {
        this.props.agreeAndContinue(this.props.signingDocumentId);
    };

    handleBottomVisible = () => {
        this.props.onBottomVisible(this.props.signingDocumentId);
    };

    renderHeader() {
        const {
            signingError,
            signingTitle,
            signingSubTitle,
            signingDescription
        } = this.props;
        if (signingError) return null;

        const downloadIcon = this.renderDownloadIcon();
        const downloadButton = this.renderDownloadButton();
        return (
            <header className={styles.header}>
                <div
                    className={styles.title}
                    data-testid="sign-documents-modal-title"
                >
                    <div>
                        <div className={styles.mobileWrapper}>
                            <h1>
                                {signingTitle}{' '}
                                {getCountry() === 'UK' ? (
                                    <span>{signingSubTitle}</span>
                                ) : null}
                            </h1>
                            <IwMedia to="small">{downloadIcon}</IwMedia>
                        </div>
                        {signingDescription && (
                            <h2 data-testid="sign-documents-modal-description">
                                {signingDescription}
                            </h2>
                        )}
                        <h1 className={styles.subtitle}>
                            <FormattedMessage
                                id="_.draw_down.sign_documents.please_read_to_sign"
                                defaultMessage='Please read to the bottom of the agreement and click "sign" to continue.'
                            />
                        </h1>
                    </div>
                    <IwMedia from="small">{downloadButton}</IwMedia>
                </div>
            </header>
        );
    }

    renderContent() {
        const { signingError, signingReady, signingDocumentContent } =
            this.props;

        if (signingError) {
            return (
                <div className={styles.errorMessage}>
                    <FormattedMessage
                        id="_.draw_down.sign_documents.unable_to_get_documents"
                        defaultMessage="We were unable to complete the signing documents process. Your account manager will be in contact soon, or give us a call on this number 020 3778 0105."
                    />
                </div>
            );
        }

        if (signingReady) {
            const content = { __html: signingDocumentContent };
            return <div dangerouslySetInnerHTML={content} />;
        }

        return <SigningDocumentContentPending />;
    }

    renderDownloadIcon() {
        const { signingDownloadURL } = this.props;
        return signingDownloadURL ? (
            <a
                href={signingDownloadURL}
                data-testid="download-pdf-button-mobile"
                className={styles.downloadIcon}
                download
            >
                <DownloadButton
                    pending={false}
                    className={styles.downloadButton}
                    iconOnly={true}
                />
            </a>
        ) : null;
    }

    renderDownloadButton() {
        const { signingDownloadURL } = this.props;
        if (signingDownloadURL) {
            return (
                <div className={styles.downloadWrapper}>
                    <a
                        href={signingDownloadURL}
                        data-testid="download-pdf-button"
                        download
                    >
                        <DownloadButton
                            pending={false}
                            className={styles.downloadButton}
                        />
                    </a>
                </div>
            );
        } else {
            return null;
        }
    }

    disabledConfirmButton() {
        const {
            signingReady,
            signingContinueEnabled,
            signingError,
            signingCheckBox
        } = this.props;

        if (getCountry() === 'DE') {
            return (
                !signingReady ||
                !signingContinueEnabled ||
                signingError ||
                !signingCheckBox
            );
        }

        return !signingReady || !signingContinueEnabled || signingError;
    }

    render() {
        const {
            signingReady,
            signingDocumentStatus,
            signingAgreePending,
            signingCheckBox,
            documentType
        } = this.props;
        const content = this.renderContent();

        return (
            <div className={styles.Modal} data-testid="sign-documents-modal">
                {this.renderHeader()}
                <ScrollPanel
                    type="article"
                    enabled={
                        typeof signingReady !== 'undefined'
                            ? signingReady.toString()
                            : 'false'
                    }
                    onBottomVisible={this.handleBottomVisible}
                    data-testid="sign-documents-modal-content"
                >
                    {content}
                    {getCountry() === 'DE' &&
                    documentType === 'master_loan_agreement' ? (
                        <div className={styles.checkbox}>
                            <CheckBox
                                id="terms-checkbox"
                                checked={signingCheckBox}
                                onChange={() =>
                                    this.props.toggleCheckbox(!signingCheckBox)
                                }
                                label={
                                    <span className={styles.termstext}>
                                        <FormattedMessage
                                            id="_.draw_down.sign_documents.mla_terms_agreement"
                                            defaultMessage="Ich habe den Darlehensvertrag gelesen und stimme den {link} zu"
                                            values={{
                                                link: (
                                                    <a
                                                        href="https://www.iwoca.de/allgemeine_geschaeftsbedingungen/"
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                    >
                                                        <FormattedMessage
                                                            id="_.draw_down.sign_documents.mla_terms_and_conditions"
                                                            defaultMessage="Allgemeinen Geschäftsbedingungen"
                                                        />
                                                    </a>
                                                )
                                            }}
                                        />
                                    </span>
                                }
                            />
                        </div>
                    ) : null}
                </ScrollPanel>
                <Prompt className={styles.modalPrompt}>
                    <button onClick={this.handleCancel}>
                        <FormattedMessage
                            id="_.draw_down.sign_documents.cancel"
                            defaultMessage="Cancel"
                        />
                    </button>
                    <ConfirmationButton
                        onClick={this.handleAgree}
                        status={signingDocumentStatus}
                        loading={signingAgreePending}
                        disabled={this.disabledConfirmButton()}
                    >
                        <FormattedMessage
                            id="_.draw_down.sign_documents.sign"
                            defaultMessage="Sign"
                        />
                    </ConfirmationButton>
                </Prompt>
            </div>
        );
    }
}

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SignDocumentsActionModal);
